// features/notifications/filterNotifications.ts — pure list helpers (no React, no store).

import type { Notification, NotificationType } from '@/core/models/notification';

export type ScopeId = 'all' | 'unread' | 'following' | 'mentions';
export type TypeFilter = 'all' | NotificationType;

//: The "Follows" tab/count is one umbrella over all three follow-family
// types — a request and its acceptance are still "something about someone
// following you" from the recipient's point of view, and splitting them into
// invisible sub-tabs would just make two of the three follow types
// unreachable from the filter UI.
const FOLLOW_FAMILY_TYPES: readonly NotificationType[] = ['follow', 'follow_request', 'follow_accepted'];
// Likes were merged into Cherish: the Cherishes tab also holds the older "like" rows.
const CHERISH_FAMILY_TYPES: readonly NotificationType[] = ['cherish', 'like'];

/** AND-combine a type filter and a scope filter over a notifications list. */
export function filterNotifications(
  list: Notification[],
  type: TypeFilter,
  scope: ScopeId,
): Notification[] {
  return list.filter((n) => {
    const typeOk =
      type === 'all'
        ? true
        : type === 'follow'
          ? (FOLLOW_FAMILY_TYPES as NotificationType[]).includes(n.type)
          : type === 'cherish'
            ? (CHERISH_FAMILY_TYPES as NotificationType[]).includes(n.type)
            : n.type === type;
    if (!typeOk) return false;
    switch (scope) {
      case 'unread':    return !n.read;
      case 'following': return n.fromFollowed === true;
      case 'mentions':  return n.type === 'mention';
      case 'all':
      default:          return true;
    }
  });
}

export interface ActivityCounts {
  likes: number;
  replies: number;
  mentions: number;
  followers: number;
}

/** Count notifications by type for the Activity Overview panel. */
export function activityCounts(list: Notification[]): ActivityCounts {
  const count = (t: NotificationType) => list.filter((n) => n.type === t).length;
  return {
    likes: count('like') + count('cherish'),
    replies: count('reply'),
    mentions: count('mention'),
    followers: list.filter((n) => (FOLLOW_FAMILY_TYPES as NotificationType[]).includes(n.type))
      .length,
  };
}
